import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const messagesContainerRef = useRef(null);

    // Messages d'accueil initiaux avec options
    const initialMessages = [
        {
            id: 1,
            text: "🤖 **ApplicoBot** - Assistant IA ApplicodeTech\n\nJe suis votre assistant virtuel spécialisé en technologie et innovation. Comment puis-je vous aider aujourd'hui ?",
            sender: 'bot',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        },
        {
            id: 2,
            text: "Je peux vous parler de :\n• ApplicodeTech et nos services\n• Développement web/mobile\n• Cloud & DevOps\n• IA & Machine Learning\n• Cybersécurité\n• Toutes technologies IT\n• Et bien plus !",
            sender: 'bot',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
    ];

    // Base de connaissances améliorée avec synonymes
    const knowledgeBase = {
        "applicodetech": {
            keywords: ["applicodetech", "entreprise", "startup", "société", "qui êtes-vous", "présentez-vous", "à propos"],
            responses: [
                "**ApplicodeTech** est une start-up IT sénégalaise innovante spécialisée dans le développement de solutions technologiques sur mesure pour les entreprises. 🚀\n\nNous offrons :\n• Développement Full-Stack\n• Solutions Cloud\n• Cybersécurité\n• IA & Machine Learning\n• DevOps & CI/CD\n• Blockchain & Web3",
                "ApplicodeTech est votre partenaire digital au Sénégal, créant des solutions IT innovantes depuis Saint-Louis. Nous combinons expertise technique et vision business pour transformer vos idées en réalité numérique. 💻",
                "Fondée à Saint-Louis, ApplicodeTech est une entreprise tech sénégalaise qui révolutionne le paysage numérique avec des solutions sur mesure et une expertise de pointe. 🌟"
            ]
        },
        "services": {
            keywords: ["services", "offres", "proposez", "faites", "développement", "prestations", "solutions", "produits"],
            responses: [
                "**Nos services principaux** :\n\n1. **📱 Développement Full-Stack**\n   - Applications Web (React/Next.js/Vue)\n   - Applications Mobile (React Native/Flutter)\n   - APIs & Microservices\n   - E-commerce & Marketplaces\n\n2. **☁️ Cloud & DevOps**\n   - Architecture Cloud AWS/Azure/GCP\n   - Conteneurisation (Docker/Kubernetes)\n   - CI/CD Pipelines\n   - Infrastructure as Code\n\n3. **🤖 IA & Data Science**\n   - Machine Learning Models\n   - Computer Vision\n   - Natural Language Processing\n   - Big Data Analytics\n\n4. **🔒 Cybersécurité**\n   - Audit Sécurité\n   - Pentesting\n   - Protection Données\n   - Formation Sécurité\n\n5. **⛓️ Blockchain & Web3**\n   - Smart Contracts\n   - DApps\n   - NFTs\n   - Tokenization"
            ]
        },
        "contact": {
            keywords: ["contact", "devis", "joindre", "téléphone", "email", "adresse", "localisation", "prix", "tarif"],
            responses: [
                "**Contact ApplicodeTech** 📞\n\n• **Téléphone** : +221 76 609 19 32\n• **Email** : contact@applicodetech.com\n• **Adresse** : Saint-Louis, Sénégal\n• **Site web** : https://applicodetech.sn\n• **Heures** : Lundi-Vendredi 8h-18h\n• **Urgences** : Support 24/7 disponible\n\nSouhaitez-vous que je vous mette en relation avec un conseiller ?"
            ]
        },
        "developpement": {
            keywords: ["développement", "programmation", "coder", "code", "software", "logiciel", "application", "app"],
            responses: [
                "**Développement logiciel** 💻\n\nNous maîtrisons les technologies modernes :\n\n• **Frontend** : React, Vue.js, Angular, Next.js\n• **Backend** : Node.js, Python, Java, PHP\n• **Mobile** : React Native, Flutter, Kotlin, Swift\n• **Base de données** : PostgreSQL, MongoDB, MySQL\n• **APIs** : REST, GraphQL, WebSockets\n\nQuel type d'application souhaitez-vous développer ?"
            ]
        },
        "cloud": {
            keywords: ["cloud", "aws", "azure", "gcp", "hébergement", "serveur", "infrastructure", "devops"],
            responses: [
                "**Cloud & DevOps** ☁️\n\nNos expertises :\n\n• **Cloud Providers** : AWS, Azure, Google Cloud\n• **Containerisation** : Docker, Kubernetes\n• **CI/CD** : Jenkins, GitLab CI, GitHub Actions\n• **Infrastructure as Code** : Terraform, CloudFormation\n• **Monitoring** : Prometheus, Grafana, Datadog\n\nNous vous aidons à migrer et optimiser votre infrastructure cloud !"
            ]
        },
        "ia": {
            keywords: ["ia", "intelligence artificielle", "machine learning", "deep learning", "ai", "data", "données"],
            responses: [
                "**Intelligence Artificielle & Data** 🤖\n\nNos solutions IA :\n\n• **Machine Learning** : Prédiction, Classification, Clustering\n• **Computer Vision** : Reconnaissance d'images, Détection d'objets\n• **NLP** : Chatbots, Analyse de sentiment, Traduction\n• **Big Data** : Spark, Hadoop, Data Lakes\n• **BI** : Tableaux de bord, Analytics, Reporting\n\nDonnez vie à vos données avec l'IA !"
            ]
        },
        "securite": {
            keywords: ["sécurité", "cybersécurité", "hacking", "pentest", "protection", "audit", "rgpd"],
            responses: [
                "**Cybersécurité** 🔒\n\nNos prestations :\n\n• **Audit de sécurité** : Analyse de vulnérabilités\n• **Pentesting** : Tests d'intrusion web/applicatif\n• **Sécurité Cloud** : Configuration sécurisée AWS/Azure\n• **Formation** : Sensibilisation aux risques\n• **Conformité** : RGPD, PCI-DSS, ISO 27001\n\nProtégez vos données et votre infrastructure !"
            ]
        },
        "react": {
            keywords: ["react", "reactjs", "frontend", "javascript", "js", "component"],
            responses: [
                "**React.js** ⚛️\n\nFramework JavaScript pour interfaces utilisateur :\n\n• **Composants réutilisables**\n• **Virtual DOM** pour performances optimales\n• **Hooks** (useState, useEffect, useContext)\n• **State Management** (Redux, Context API)\n• **Next.js** pour SSR et SSG\n\nReact est parfait pour des applications web modernes et réactives !"
            ]
        },
        "default": {
            keywords: [],
            responses: [
                "🤔 Je ne suis pas sûr de comprendre. Pourriez-vous reformuler ?\n\nJe peux vous aider avec :\n• Informations sur ApplicodeTech\n• Questions techniques IT\n• Conseils sur les technologies\n• Explications sur nos services\n• Coordonnées de contact\n\nEssayez des mots-clés comme : développement, cloud, IA, sécurité, contact, devis...",
                "Désolé, je n'ai pas bien saisi votre demande. 😕\n\nVoici ce que je peux faire pour vous :\n• Présenter ApplicodeTech\n• Décrire nos services\n• Donner nos coordonnées\n• Répondre à vos questions tech\n\nQue souhaitez-vous savoir ?",
                "Je n'ai pas la réponse à cette question précise. Cependant, je peux vous renseigner sur :\n• Nos services de développement\n• Solutions Cloud et DevOps\n• Intelligence Artificielle\n• Cybersécurité\n• Comment nous contacter"
            ]
        }
    };

    // Fonction pour trouver la meilleure réponse avec scoring amélioré
    const findBestResponse = (input) => {
        const inputLower = input.toLowerCase();
        let bestMatch = { category: 'default', score: 0 };

        Object.entries(knowledgeBase).forEach(([category, data]) => {
            data.keywords.forEach(keyword => {
                if (inputLower.includes(keyword)) {
                    // Score plus élevé pour les correspondances exactes
                    const score = keyword.length > 3 ? 10 : 5;
                    if (score > bestMatch.score) {
                        bestMatch = { category, score };
                    }
                }
            });
        });

        const responses = knowledgeBase[bestMatch.category].responses;
        return responses[Math.floor(Math.random() * responses.length)];
    };

    // Fonction pour simuler la frappe du bot
    const typeMessage = async (text, messageId) => {
        const words = text.split(' ');
        let currentText = '';

        for (let word of words) {
            currentText += word + ' ';
            setMessages(prev => prev.map(msg =>
                msg.id === messageId
                    ? { ...msg, text: currentText }
                    : msg
            ));
            await new Promise(resolve =>
                setTimeout(resolve, Math.random() * 30 + 20)
            );
        }
    };

    // Fonction pour envoyer un message avec gestion d'erreurs
    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;

        const userMessageText = inputMessage;

        // Ajouter le message de l'utilisateur
        const userMessage = {
            id: messages.length + 1,
            text: userMessageText,
            sender: 'user',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsTyping(true);
        setIsLoading(true);

        try {
            // Simuler le temps de réponse du bot
            await new Promise(resolve => setTimeout(resolve, 1000));

            const botResponse = findBestResponse(userMessageText);

            const botMessage = {
                id: messages.length + 2,
                text: botResponse,
                sender: 'bot',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };

            setMessages(prev => [...prev, botMessage]);

            // Simuler l'effet de frappe
            // await typeMessage(botResponse, botMessage.id);

            // Sauvegarder dans l'historique
            setChatHistory(prev => [...prev, {
                question: userMessageText,
                answer: botResponse,
                timestamp: new Date().toISOString()
            }]);

            // Sauvegarder dans localStorage
            saveMessagesToStorage([...messages, userMessage, botMessage]);

        } catch (error) {
            console.error('Erreur lors de l\'envoi du message:', error);

            // Message d'erreur pour l'utilisateur
            const errorMessage = {
                id: messages.length + 2,
                text: "Désolé, une erreur s'est produite. Veuillez réessayer.",
                sender: 'bot',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsTyping(false);
            setIsLoading(false);
        }
    };

    // Fonction pour les suggestions rapides
    const handleQuickReply = (text) => {
        setInputMessage(text);
        setTimeout(() => handleSendMessage(), 100);
    };

    // Fonction pour effacer la conversation
    const clearChat = () => {
        setMessages(initialMessages);
        localStorage.removeItem('chatMessages');
    };

    // Fonction pour sauvegarder les messages
    const saveMessagesToStorage = (messagesToSave) => {
        try {
            localStorage.setItem('chatMessages', JSON.stringify(messagesToSave));
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
        }
    };

    // Fonction pour la reconnaissance vocale (simulée)
    const startVoiceInput = () => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            setIsListening(true);
            // Implémentation réelle de la reconnaissance vocale
            // Pour l'exemple, on simule une entrée
            setTimeout(() => {
                setIsListening(false);
                setInputMessage("Recherche vocale...");
            }, 2000);
        } else {
            alert("La reconnaissance vocale n'est pas supportée par votre navigateur.");
        }
    };

    // Suggestions rapides élargies
    const quickReplies = [
        "Qui est ApplicodeTech ?",
        "Vos services en détails",
        "Contact et devis",
        "Expliquez-moi React",
        "Parlez-moi de l'IA",
        "Cloud et DevOps",
        "Cybersécurité"
    ];

    // Charger les messages depuis localStorage
    useEffect(() => {
        try {
            const savedMessages = localStorage.getItem('chatMessages');
            if (savedMessages) {
                setMessages(JSON.parse(savedMessages));
            }
        } catch (error) {
            console.error('Erreur lors du chargement:', error);
        }
    }, []);

    // Initialiser les messages si vide
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages(initialMessages);
        }
    }, [isOpen, messages.length]);

    // Scroll automatique vers le bas
    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, [messages, isTyping, isLoading]);

    // Focus sur l'input quand le chat s'ouvre
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 300);
        }
    }, [isOpen]);

    return (
        <div className="chatbot-wrapper">
            {/* Bouton du chatbot avec robot animé */}
            <button
                className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? "Fermer le chatbot" : "Ouvrir le chatbot"}
                aria-expanded={isOpen}
                style={{ cursor: 'pointer' }}
            >
                <div className="robot-container">
                    <div className="robot-antenna"></div>
                    <div className="robot-head">
                        <div className="robot-eyes">
                            <div className="robot-eye"></div>
                            <div className="robot-eye"></div>
                        </div>
                    </div>
                    <div className="robot-body">
                        <div className="robot-arms">
                            <div className="robot-arm"></div>
                            <div className="robot-arm"></div>
                        </div>
                        <div className="robot-screen"></div>
                    </div>
                    <div className="robot-badge">AI</div>
                </div>
            </button>

            {/* Fenêtre du chatbot */}
            <div className={`chatbot-container ${isOpen ? 'open' : ''}`}>
                {/* En-tête avec contrôles */}
                <div className="chatbot-header">
                    <h3 className="bot-name">ApplicoBot AI</h3>
                    <div className="header-controls">
                        <button
                            className="clear-chat"
                            onClick={clearChat}
                            title="Effacer la conversation"
                            aria-label="Effacer la conversation"
                        >
                            🗑️
                        </button>
                        <button
                            className="close-button"
                            onClick={() => setIsOpen(false)}
                            aria-label="Fermer"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Zone de messages avec rôle ARIA */}
                <div
                    className="chatbot-messages"
                    ref={messagesContainerRef}
                    role="log"
                    aria-live="polite"
                    aria-label="Messages du chat"
                >
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`message ${message.sender}`}
                        >
                            <div className="message-content">
                                <div className="message-text">{message.text}</div>
                                <div className="message-timestamp">{message.timestamp}</div>
                            </div>
                        </div>
                    ))}

                    {isTyping && (
                        <div className="message bot typing">
                            <div className="typing-indicator">
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                            </div>
                        </div>
                    )}

                    {isLoading && (
                        <div className="loading-spinner-container">
                            <div className="loading-spinner"></div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Suggestions rapides */}
                <div className="quick-replies">
                    {quickReplies.map((reply, index) => (
                        <button
                            key={index}
                            className="quick-reply"
                            onClick={() => handleQuickReply(reply)}
                        >
                            {reply}
                        </button>
                    ))}
                </div>

                {/* Zone de saisie avec contrôles avancés */}
                <div className="chatbot-input">
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputMessage}
                        onChange={(e) => {
                            if (e.target.value.length <= 500) {
                                setInputMessage(e.target.value);
                            }
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Posez votre question... (max 500 caractères)"
                        className="message-input"
                        maxLength={500}
                        disabled={isLoading}
                    />
                    <button
                        onClick={startVoiceInput}
                        className={`voice-button ${isListening ? 'listening' : ''}`}
                        title="Recherche vocale"
                        aria-label="Recherche vocale"
                        disabled={isLoading}
                    >
                        🎤
                    </button>
                    <button
                        onClick={handleSendMessage}
                        className="send-button"
                        disabled={!inputMessage.trim() || isLoading}
                        aria-label="Envoyer"
                    >
                        {isLoading ? (
                            <span className="loading-spinner-small"></span>
                        ) : (
                            <span className="send-icon">➤</span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Chatbot;